/**
 * "Get it by <date>" row on product cards (PLP / collection / search / any grid).
 *
 * Deliberately silent unless we already know the customer's pincode:
 *   1. the pincode they typed into the PDP Delivery Info card (localStorage), or
 *   2. the default-address pincode of a logged-in customer (rendered by Liquid).
 * With neither, no row is rendered at all - the card looks exactly as it does today.
 *
 * The date is product-independent (same origin warehouse, same Delhivery TAT),
 * so one call answers the whole grid no matter how many cards are on it.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'ta_delivery_pin'; // shared with delivery-estimator.js
  var CACHE_KEY = 'ta_delivery_eta_v2'; // v2: switched from edd_to to edd_from
  var PIN_RE = /^[1-9][0-9]{5}$/;
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  var root = document.querySelector('[data-delivery-context]');
  if (!root) return;

  var endpoint = root.dataset.endpoint || '/apps/delivery/check';

  function resolvePin() {
    try {
      var saved = localStorage.getItem(STORAGE_KEY);
      if (saved && PIN_RE.test(saved)) return saved;
    } catch (e) {
      /* private mode - fall through to the customer address */
    }
    var fromAccount = root.dataset.customerPin;
    return fromAccount && PIN_RE.test(fromAccount) ? fromAccount : null;
  }

  /**
   * Same expiry the API itself uses: the answer changes at the 11:00 IST dispatch
   * cutoff and again at midnight IST, so a flat TTL would serve a stale date
   * across either boundary.
   */
  function expiresAt() {
    var nowIST = new Date(Date.now() + 5.5 * 3600 * 1000); // UTC+5:30
    var hour = nowIST.getUTCHours();
    var minutes = nowIST.getUTCMinutes();
    var minsToCutoff = (11 - hour) * 60 - minutes;
    var minsToMidnight = (24 - hour) * 60 - minutes;
    var minsLeft = minsToCutoff > 0 ? minsToCutoff : minsToMidnight;
    return Date.now() + minsLeft * 60 * 1000;
  }

  function readCache(pin) {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (entry.pin !== pin || Date.now() > entry.expires) return null;
      return entry.label; // '' is a valid cached answer: unserviceable, show nothing
    } catch (e) {
      return null;
    }
  }

  function writeCache(pin, label) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ pin: pin, label: label, expires: expiresAt() }));
    } catch (e) {
      /* ignore */
    }
  }

  // Earliest date of the range - the grid advertises the best case; the PDP
  // card still shows the full range before anyone commits to buying.
  function formatBy(iso) {
    var d = new Date(iso + 'T00:00:00');
    if (isNaN(d)) return '';
    return d.getDate() + ' ' + MONTHS[d.getMonth()];
  }

  var label = null; // null = not resolved yet, '' = resolved to "show nothing"

  function paint() {
    if (!label) return;
    var text = 'Get it by ' + label;
    document.querySelectorAll('[data-plp-eta]:not([data-plp-eta-filled])').forEach(function (slot) {
      slot.textContent = text;
      slot.hidden = false;
      slot.setAttribute('data-plp-eta-filled', 'true');
    });
  }

  function start() {
    // No product cards on this page (e.g. cart, policy pages) - nothing to fetch for.
    if (!document.querySelector('[data-plp-eta]')) return;

    var pin = resolvePin();
    if (!pin) return;

    var cached = readCache(pin);
    if (cached !== null) {
      label = cached;
      paint();
      watchForNewCards();
      return;
    }

    fetch(endpoint + '?pin=' + encodeURIComponent(pin), { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        label = data && data.ok && data.serviceable ? formatBy(data.edd_from) : '';
        writeCache(pin, label);
        paint();
      })
      .catch(function () {
        // Upstream down. Leave the cards bare rather than promising a date we
        // couldn't confirm, and don't cache the failure.
        label = '';
      });

    watchForNewCards();
  }

  // Infinite scroll, filter re-renders and quick-view all inject cards after load.
  var observing = false;
  function watchForNewCards() {
    if (observing || !window.MutationObserver) return;
    observing = true;

    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        paint();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
